import {
  Plus,
  Building,
  CheckCircle,
  Trash2,
  Loader2,
  AlertCircle,
  Settings,
  X,
  Search,
  Database,
  Send,
  Folder,
  Wrench,
  Terminal,
} from "lucide-react";
import { useState, useEffect, useMemo, useRef } from "react";
import { integrationService } from "../services/IntegrationService";
import {
  IntegrationServer,
  IntegrationAccess,
  IntegrationServerInstance,
  IntegrationServerSchema,
  SelectOptionResponseSchema,
  ConnectorSurfaceKindSchema,
  type SelectOptionResponse,
  type ConnectorCatalogSurface,
  type ConnectorSurface,
  type ConnectorSurfaceKind,
} from "@/schema";
import { StatusModal } from "./StatusModal";
import { IntegrationSelectionModal } from "./IntegrationSelectionModal";
import {
  CustomIntegrationModal,
  type CustomIntegrationSubmitPayload,
} from "./CustomIntegrationModal";
import { useUser as useClerkUser } from "@clerk/clerk-react";

interface IntegrationManagerProps {
  isOpen: boolean;
  onClose?: () => void;
  variant?: "list" | "addModal";
  surface?: "card" | "section";
  onAddSource?: () => void;
  onChanged?: () => void;
  refreshKey?: number;
  showHeader?: boolean;
  requireAnchor?: boolean;
  onboardingError?: string | null;
  /** P4-P5: pre-target a server (the openAuth box's serverId) → auto-open ITS connect,
   *  SKIPPING the catalog grid. The agent already chose the integration; the user only auths. */
  focusServerId?: string;
  focusInstanceId?: string;
  onConnectorIntent?: (intent: {
    kind: ConnectorFilter;
    query?: string;
    serverName?: string;
    label?: string;
  }) => Promise<void> | void;
  onRegisteredSurfaces?: (result: {
    surfaces: ConnectorSurface[];
    announce?: { status: string; conversationId?: string };
  }) => Promise<void> | void;
  conversationId?: string;
}

const formatDate = (value?: string) => {
  if (!value) return "Date unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return date.toLocaleDateString();
};

const NEW_INSTANCE_ACCESS: IntegrationAccess = {
  tools: { read: true, write: false },
  entities: { mode: "all", allow: [] },
};

const newInstanceAccess = (): IntegrationAccess => ({
  tools: { ...NEW_INSTANCE_ACCESS.tools },
  entities: {
    mode: NEW_INSTANCE_ACCESS.entities.mode,
    allow: [...NEW_INSTANCE_ACCESS.entities.allow],
  },
});

const parseEmbeddedErrorJson = (value: string): unknown => {
  const start = value.indexOf("{");
  const end = value.lastIndexOf("}");
  if (start < 0 || end <= start) return undefined;
  try {
    return JSON.parse(value.slice(start, end + 1));
  } catch {
    return undefined;
  }
};

const errorTextOf = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === "string") {
    const embedded = parseEmbeddedErrorJson(value);
    const embeddedText = errorTextOf(embedded);
    return embeddedText || value;
  }
  if (value instanceof Error) return value.message;
  if (typeof value !== "object") return undefined;
  const record = value as Record<string, unknown>;
  return (
    errorTextOf(record.message) ||
    errorTextOf(record.error) ||
    errorTextOf(record.reason) ||
    errorTextOf(record.data) ||
    errorTextOf(record.response) ||
    errorTextOf(record.body)
  );
};

const connectorErrorMessage = (err: unknown, fallback: string): string => {
  const message = errorTextOf(err) || fallback;
  return message.length > 900 ? `${message.slice(0, 900)}...` : message;
};

// The connector kind filters PROJECT the shared @causit/integration-schema
// declared set (`surface` and `command` arrive by construction) — never a
// local vocabulary. Icons/labels are presentation only.
type ConnectorFilter = ConnectorSurfaceKind;

const CONNECTOR_FILTER_FACE: Record<ConnectorFilter, { label: string; Icon: typeof Database }> = {
  surface: { label: "Surfaces", Icon: Database },
  channel: { label: "Channels", Icon: Send },
  file: { label: "File buckets", Icon: Folder },
  tool: { label: "Tools", Icon: Wrench },
  command: { label: "Commands", Icon: Terminal },
};

const CONNECTOR_FILTERS: {
  id: ConnectorFilter;
  label: string;
  Icon: typeof Database;
}[] = (ConnectorSurfaceKindSchema.options as ConnectorFilter[]).map((id) => ({
  id,
  ...CONNECTOR_FILTER_FACE[id],
}));

// a server supports a kind exactly when its DECLARED scopeRule names it — the
// shared schema declaration decides, never an abilities sniff.
const serverSupportsKind = (
  server: IntegrationServer,
  kind: ConnectorFilter
): boolean => kind in (server.scopeRule || {});

const surfaceLabel = (kind: ConnectorFilter) =>
  CONNECTOR_FILTERS.find((item) => item.id === kind)?.label || kind;

const connectorSurfaceTitle = (surface: ConnectorCatalogSurface): string => (
  surface.providerSurfaceLabel ||
  surface.providerSurfaceName ||
  surface.providerSurfaceId ||
  surfaceLabel(surface.kind)
);

const cleanConnectorSurfaceCopy = (value?: string): string => (
  (value || "")
    .replace(/^Priority\s+OData\s+(surface|entity\s+set)\s+/i, "")
    .trim()
);

const connectorSurfaceDisplayTitle = (surface: ConnectorCatalogSurface): string => (
  cleanConnectorSurfaceCopy(
    surface.providerSurfaceName ||
      surface.providerSurfaceLabel ||
      surface.providerSurfaceId,
  ) ||
  connectorSurfaceTitle(surface)
);

const connectorSurfaceDisplayDescription = (
  surface: ConnectorCatalogSurface,
): string => {
  const title = connectorSurfaceDisplayTitle(surface);
  const rawDescription = surface.providerSurfaceDescription || "";
  const description = cleanConnectorSurfaceCopy(
    rawDescription,
  );
  if (description && description !== title) return description;
  if (/^Priority\s+OData\s+surface/i.test(rawDescription)) {
    return "OData surface";
  }
  const providerId = cleanConnectorSurfaceCopy(surface.providerSurfaceId);
  if (providerId && providerId !== title) return providerId;
  return surfaceLabel(surface.kind);
};

const SURFACE_TILE_HEIGHT = 74;
const SURFACE_ROW_GAP = 12;
const SURFACE_ROW_STRIDE = SURFACE_TILE_HEIGHT + SURFACE_ROW_GAP;
const SURFACE_OVERSCAN_ROWS = 3;

export const IntegrationManager = ({
  isOpen,
  onClose,
  variant = "list",
  surface = "card",
  onAddSource,
  onChanged,
  onRegisteredSurfaces,
  refreshKey,
  showHeader = true,
  requireAnchor = false,
  onboardingError = null,
  focusServerId,
  focusInstanceId,
  onConnectorIntent,
  conversationId,
}: IntegrationManagerProps) => {
  const { user: clerkUser } = useClerkUser();
  const [instances, setInstances] = useState<IntegrationServerInstance[]>([]);
  const [isLoadingConnections, setIsLoadingConnections] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [disconnectingIds, setDisconnectingIds] = useState<Set<string>>(
    new Set()
  );

  const [servers, setServers] = useState<IntegrationServer[]>([]);
  const [isLoadingServers, setIsLoadingServers] = useState(false);
  const [serversError, setServersError] = useState<string | null>(null);
  const [connectorFilter, setConnectorFilter] =
    useState<ConnectorFilter>("surface");
  const [connectorSearch, setConnectorSearch] = useState("");
  const [pendingConnectorRequest, setPendingConnectorRequest] = useState<
    string | null
  >(null);
  const [focusedInstanceId, setFocusedInstanceId] = useState<string | null>(
    focusInstanceId || null
  );
  const [focusedSurfaces, setFocusedSurfaces] = useState<ConnectorCatalogSurface[]>([]);
  const [surfaceQuery, setSurfaceQuery] = useState("");
  const [isLoadingSurfaces, setIsLoadingSurfaces] = useState(false);
  const [selectedSurfaceIds, setSelectedSurfaceIds] = useState<Set<string>>(
    new Set()
  );

  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error" | "waiting";
    title?: string;
    message: string;
  } | null>(null);
  const [isRegisteringSurfaces, setIsRegisteringSurfaces] = useState(false);

  // Custom auth modal state
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customServer, setCustomServer] = useState<IntegrationServer | null>(
    null
  );
  const customSubmitResultRef = useRef<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const connectedInstanceIdRef = useRef<string | null>(null);

  const resetCustomModal = () => {
    setShowCustomModal(false);
    setCustomServer(null);
    customSubmitResultRef.current = null;
    connectedInstanceIdRef.current = null;
  };

  // Selection modal state
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [selectionInstanceId, setSelectionInstanceId] = useState<string | null>(
    null
  );

  // Handle OAuth callback - check URL params once when opened
  useEffect(() => {
    if (!isOpen) return;

    const url = new URL(window.location.href);
    const integrationStatus = url.searchParams.get("integrationStatus") as
      | "connected"
      | "selectionPending"
      | "failed"
      | null;
    const message = url.searchParams.get("message");
    const instanceId = url.searchParams.get("instanceId");
    if (integrationStatus !== null) {
      // Track the callback

      // Handle based on status
      if (integrationStatus === "selectionPending") {
        if (integrationStatus === "selectionPending" && instanceId) {
          setSelectionInstanceId(instanceId);
          setShowSelectionModal(true);
        }
      } else if (
        integrationStatus === "connected" ||
        integrationStatus === "failed"
      ) {
        const isSuccess = integrationStatus === "connected";
        setStatusMessage({
          type: isSuccess ? "success" : "error",
          message:
            message ||
            (isSuccess
              ? "Connector connected successfully!"
              : "Failed to connect connector"),
        });

        setTimeout(() => setStatusMessage(null), isSuccess ? 3000 : 5000);
        if (isSuccess) {
          if (instanceId) {
            setFocusedInstanceId(instanceId);
          }
          onChanged?.();
        }
      }

      // Clean up URL parameters AFTER handling
      url.searchParams.delete("integrationStatus");
      url.searchParams.delete("message");
      url.searchParams.delete("instanceId");
      url.searchParams.delete("serverName");
      window.history.replaceState({}, "", url.toString());
    }
  }, [isOpen]);

  const loadInstances = async () => {
    try {
      setIsLoadingConnections(true);
      setError(null);

      const apiInstances = await integrationService.getInstances(true);
      setInstances(apiInstances);
    } catch (err) {
      setError("Failed to load connections");
    } finally {
      setIsLoadingConnections(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadInstances();
    }
  }, [isOpen, refreshKey]);

  useEffect(() => {
    if (!isOpen) {
      setShowCustomModal(false);
      setCustomServer(null);
      customSubmitResultRef.current = null;
    }
  }, [isOpen]);

  const loadServers = async () => {
    try {
      setIsLoadingServers(true);
      setServersError(null);
      const servers = await integrationService.getServers();
      setServers(servers.map((s) => IntegrationServerSchema.parse(s)));
    } catch (err: any) {
      setServersError(connectorErrorMessage(err, "Failed to load connectors"));
    } finally {
      setIsLoadingServers(false);
    }
  };

  useEffect(() => {
    if (isOpen && variant === "addModal" && !isLoadingServers && servers.length === 0) {
      loadServers();
    }
  }, [isOpen, variant]);

  useEffect(() => {
    setFocusedInstanceId(focusInstanceId || null);
  }, [focusInstanceId]);

  const connectToServer = async (server: IntegrationServer) => {
    const serverName = server?.name || "unknown";


    if (server.auth?.type === "auto") {
      try {
        setIsConnecting(true);
        setStatusMessage(null);

        // Pass user context — backend resolves fields via resolveFrom
        const userParams: Record<string, string> = {};
        const userEmail =
          clerkUser?.primaryEmailAddress?.emailAddress ||
          clerkUser?.emailAddresses?.[0]?.emailAddress;
        if (userEmail) userParams.email = userEmail;

        const response = await integrationService.submitCustomIntegration(
          server.name,
          {}, // no values — resolved by backend from userParams
          undefined,
          undefined,
          false,
          { userParams },
          newInstanceAccess()
        );

        await loadInstances();
        if (response?.instanceId) {
          setFocusedInstanceId(response.instanceId);
        }

        setStatusMessage({
          type: "success",
          message: `${
            server.displayInfo?.title || server.name
          } connected successfully`,
        });
        onChanged?.();

        setTimeout(() => setStatusMessage(null), 3000);
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.error || err?.message || "Failed to connect";

        setStatusMessage({ type: "error", message: errorMessage });
        setTimeout(() => setStatusMessage(null), 5000);
      } finally {
        setIsConnecting(false);
      }
      return;
    }

    if (server.auth?.type === "oauth") {
      setStatusMessage(null);
      setCustomServer(server);
      setShowCustomModal(true);
      return;
    }

    if (server.auth?.type === "custom") {
      setStatusMessage(null);
      setCustomServer(server);
      setShowCustomModal(true);
      return;
    }

    try {
      setIsConnecting(true);
      setStatusMessage(null);

      const response = await integrationService.connectToServer(
        serverName,
        server.additional || {},
        window.location.href,
        newInstanceAccess()
      );

      if (response.url) {
        window.location.href = response.url;
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.error || err?.message || "Failed to connect";

      setStatusMessage({
        type: "error",
        message: errorMessage,
      });


      setTimeout(() => {
        setStatusMessage(null);
      }, 3000);
    } finally {
      setIsConnecting(false);
    }
  };

  // P4-P5: openAuth pre-targets a server (focusServerId) → auto-open ITS connect, SKIPPING
  // the catalog grid. The agent already chose the integration; the user lands on the auth
  // form. Fires once per open, when the servers have loaded; matches name up to -/_ casing.
  const autoConnectedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) { autoConnectedRef.current = false; return; }
    if (!focusServerId || autoConnectedRef.current || servers.length === 0) return;
    const norm = (s: string) => s.toLowerCase().replace(/[-_]/g, "");
    const fid = norm(focusServerId);
    const server = servers.find((s) => {
      const n = norm(String(s.name || ""));
      return !!n && (fid.includes(n) || n.includes(fid));
    });
    if (server) {
      autoConnectedRef.current = true;
      const connectedInstance = instances.find((instance) => instance.serverName === server.name);
      const connectedInstanceId = connectedInstance?.instanceId || server.instanceIds?.[0];
      if (connectedInstanceId) {
        setFocusedInstanceId(connectedInstanceId);
        return;
      }
      void connectToServer(server);
    }
  }, [isOpen, focusServerId, servers, instances]);

  useEffect(() => {
    if (!isOpen || !focusedInstanceId) {
      setFocusedSurfaces([]);
      setSelectedSurfaceIds(new Set());
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setIsLoadingSurfaces(true);
        setFocusedSurfaces([]);
        setSelectedSurfaceIds(new Set());
        // THE LIST IS WORLD DATA: ONE read of the instance's world-held
        // catalog (synced at registration) — the complete list arrives in one
        // download; search/select-all are local over the same set.
        const surfaces = await integrationService.getSurfaces(focusedInstanceId);
        if (cancelled) return;
        setFocusedSurfaces(surfaces);
      } catch (err: any) {
        if (!cancelled) {
          const message = connectorErrorMessage(err, "Failed to load connector surfaces");
          if (/instance not found/i.test(message)) {
            setFocusedInstanceId(null);
            setFocusedSurfaces([]);
            setSelectedSurfaceIds(new Set());
            void loadInstances();
          }
          setStatusMessage({
            type: "error",
            message,
          });
        }
      } finally {
        if (!cancelled) setIsLoadingSurfaces(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, focusedInstanceId]);

  const searchQuery = connectorSearch.trim().toLowerCase();
  const visibleServers = useMemo(() => {
    if (searchQuery) {
      return servers.filter((server) =>
        [
          server.name,
          server.displayInfo?.title,
          server.displayInfo?.subtitle,
          Object.keys(server.scopeRule || {}).join(" "),
          Object.keys(server.abilities || {}).join(" "),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(searchQuery)
      );
    }

    return servers.filter((server) => serverSupportsKind(server, connectorFilter));
  }, [servers, connectorFilter, searchQuery]);

  const hasNoSearchMatches =
    searchQuery.length > 0 &&
    !isLoadingServers &&
    visibleServers.length === 0;

  const sendConnectorIntent = async (intent: {
    kind: ConnectorFilter;
    query?: string;
    serverName?: string;
    label?: string;
  }) => {
    await onConnectorIntent?.(intent);
    setPendingConnectorRequest(intent.label || intent.query || intent.serverName || "Connector");
    setStatusMessage({
      type: "success",
      message: `Building the integration for ${intent.label || intent.query || intent.serverName || "this connector"}. We'll notify you when it's ready.`,
    });
  };

  const openSearchConnectorRequest = () => {
    if (!hasNoSearchMatches) return;
    void sendConnectorIntent({
      kind: connectorFilter,
      query: connectorSearch.trim(),
      label: connectorSearch.trim(),
    });
  };

  const handleCustomIntegrationSubmit = async (
    payload: CustomIntegrationSubmitPayload
  ) => {
    if (!customServer) {
      throw new Error("No connector selected for custom authentication.");
    }

    if (customServer.auth?.type === "oauth") {
      try {
        const response = await integrationService.connectToServer(
          customServer.name,
          {
            ...(customServer.additional || {}),
            authValues: payload.values,
            instanceLabel: payload.instanceLabel,
            sandbox: payload.sandbox,
          },
          window.location.href,
          payload.access,
          payload.requiresIpWhitelist
        );
        if (response.url) {
          window.location.href = response.url;
          return;
        }
        throw new Error("OAuth provider did not return a redirect URL");
      } catch (err: any) {
        throw err instanceof Error
          ? err
          : new Error("Failed to start OAuth connector");
      }
    }

    let result: SelectOptionResponse;
    try {
      const response = await integrationService.submitCustomIntegration(
        customServer.name,
        payload.values,
        payload.instanceId,
        payload.instanceLabel,
        payload.requiresIpWhitelist,
        { sandbox: payload.sandbox },
        payload.access
      );

      result = SelectOptionResponseSchema.parse(response);
    } catch (err: any) {
      throw err instanceof Error
        ? err
        : new Error("Failed to submit custom connector");
    }

    if (result.integrationStatus !== "connected") {
      throw new Error(result.message || "Failed to connect connector");
    }


    customSubmitResultRef.current = {
      type: "success",
      message:
        result.message ||
        `${
          customServer.displayInfo?.title || customServer.name
        } connected successfully`,
    };
    connectedInstanceIdRef.current = result.instanceId;
  };

  const disconnectInstance = async (instanceId: string) => {
    const instance = instances.find((i) => i.instanceId === instanceId);

    try {
      setDisconnectingIds((prev) => new Set(prev).add(instanceId));

      const disconnectPromise = integrationService.disconnectInstance(
        instanceId,
        instance?.auth?.type === "custom" || instance?.auth?.type === "auto"
      );
      const [disconnectResult] = await Promise.allSettled([disconnectPromise]);

      if (disconnectResult.status === "rejected") {
        throw disconnectResult.reason;
      }


      await loadInstances();

      setStatusMessage({
        type: "success",
        message:
          disconnectResult.value?.message ||
          "Connector disconnected successfully",
      });

      setTimeout(() => {
        setStatusMessage(null);
      }, 3000);
    } catch (err: any) {

      setStatusMessage({
        type: "error",
        message: "Failed to disconnect connector",
      });

      setTimeout(() => {
        setStatusMessage(null);
      }, 5000);
    } finally {
      setDisconnectingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(instanceId);
        return newSet;
      });
    }
  };

  const availableSourcesContent = (
    <>
      <div className="mb-5 space-y-3">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {CONNECTOR_FILTERS.map(({ id, label, Icon }) => {
            const selected = connectorFilter === id && !searchQuery;
            return (
              <button
                key={id}
                type="button"
                onClick={() => {
                  setConnectorFilter(id);
                  setConnectorSearch("");
                }}
                className="flex min-h-11 items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors"
                style={{
                  background: selected
                    ? "var(--bg-tertiary)"
                    : "var(--bg-secondary)",
                  borderColor: selected
                    ? "#6B7EEA"
                    : "var(--border-primary)",
                  color: selected
                    ? "var(--text-primary)"
                    : "var(--text-secondary)",
                }}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{label}</span>
              </button>
            );
          })}
        </div>
        <div className="flex gap-2">
          <label
            className="flex min-h-11 flex-1 items-center gap-2 rounded-lg border px-3"
            style={{
              background: "var(--bg-secondary)",
              borderColor: "var(--border-primary)",
              color: "var(--text-primary)",
            }}
          >
            <Search className="h-4 w-4 shrink-0" style={{ color: "var(--text-muted)" }} />
            <input
              value={connectorSearch}
              onChange={(event) => setConnectorSearch(event.target.value)}
              placeholder="Search connectors"
              className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-[var(--text-muted)]"
            />
          </label>
          <button
            type="button"
            onClick={openSearchConnectorRequest}
            disabled={!hasNoSearchMatches || isConnecting}
            aria-label="Add connector from search"
            title={
              hasNoSearchMatches
                ? `Add ${connectorSearch.trim()}`
                : "Search for a connector that is not listed"
            }
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-40"
            style={{
              background: hasNoSearchMatches
                ? "var(--accent-blue)"
                : "var(--bg-secondary)",
              borderColor: hasNoSearchMatches
                ? "var(--accent-blue)"
                : "var(--border-primary)",
              color: hasNoSearchMatches ? "white" : "var(--text-secondary)",
            }}
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div
        className="rounded-lg border p-3"
        style={{
          background: "var(--bg-secondary)",
          borderColor: "var(--border-primary)",
        }}
      >
        {serversError && (
          <div className="text-sm text-red-400">{serversError}</div>
        )}
        {!serversError && isLoadingServers && (
          <div className="text-sm" style={{ color: "var(--text-muted)" }}>
            Loading available connectors...
          </div>
        )}
        {!serversError && !isLoadingServers && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {visibleServers.map((server) => {
              const filter = CONNECTOR_FILTERS.find(
                (item) => item.id === connectorFilter
              );
              const Icon = filter?.Icon || Building;
              const connectedInstance = instances.find((instance) => instance.serverName === server.name);
              const connectedInstanceId = connectedInstance?.instanceId || server.instanceIds?.[0];
              const isConnected = Boolean(connectedInstance || server.instanceStatus === "connected");
              const registeredCount = server.surfaceRegistration?.registered || 0;
              const unregisteredCount = server.surfaceRegistration?.unregistered || 0;
              const statusLabel = isConnected
                ? registeredCount > 0
                  ? `${registeredCount} registered`
                  : "Instance registered"
                : "Not registered";
              return (
                <button
                  key={server.name}
                  className="w-full min-w-0 rounded-lg border p-4 text-left transition-colors hover:border-blue-500/50 hover:bg-blue-500/10"
                  style={{
                    background: "var(--modal-bg)",
                    borderColor: "var(--border-secondary)",
                  }}
                  type="button"
                  disabled={isConnecting}
                  onClick={() => {
                    if (connectedInstanceId) {
                      setFocusedInstanceId(connectedInstanceId);
                      return;
                    }
                    void connectToServer(server);
                  }}
                >
                  <div className="mb-3 flex w-full items-start justify-between gap-3">
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                      style={{ background: "var(--accent-blue)" }}
                    >
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <span
                      className="rounded px-1.5 py-0.5 text-[10px]"
                      style={{
                        background: "var(--bg-tertiary)",
                        color: "var(--text-secondary)",
                      }}
                    >
                      {filter?.label || surfaceLabel(connectorFilter)}
                    </span>
                  </div>
                  <div className="mb-2 flex gap-2">
                    <span
                      className="rounded px-1.5 py-0.5 text-[10px]"
                      style={{
                        background: isConnected ? "rgba(34,197,94,0.14)" : "var(--bg-tertiary)",
                        color: isConnected ? "#22c55e" : "var(--text-secondary)",
                      }}
                    >
                      {statusLabel}
                    </span>
                    {isConnected && unregisteredCount > 0 && (
                      <span
                        className="rounded px-1.5 py-0.5 text-[10px]"
                        style={{
                          background: "var(--bg-tertiary)",
                          color: "var(--text-secondary)",
                        }}
                      >
                        {unregisteredCount} available
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <h4
                      className="truncate font-medium"
                      style={{ color: "var(--text-primary)" }}
                    >
                      {server.displayInfo?.title || server.name}
                    </h4>
                    <p
                      className="line-clamp-2 text-sm"
                      style={{ color: "var(--text-muted)" }}
                    >
                      {server.displayInfo?.subtitle || server.name}
                    </p>
                  </div>
                </button>
              );
            })}
            {visibleServers.length === 0 && (
              <div
                className="text-sm md:col-span-2 xl:col-span-3"
                style={{ color: "var(--text-muted)" }}
              >
                {pendingConnectorRequest
                  ? `Building the integration for ${pendingConnectorRequest}. We'll notify you when it's ready.`
                  : searchQuery
                    ? "No connectors found."
                    : "No connectors available."}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );

  const focusedInstance = focusedInstanceId
    ? instances.find((instance) => instance.instanceId === focusedInstanceId)
    : null;
  const focusedConnectorName =
    focusedInstance?.displayInfo?.title ||
    focusedInstance?.instanceName ||
    focusedInstance?.serverName ||
    focusedInstanceId ||
    "connector";

  // LOCAL search over the derived surfaces — typing narrows the list (down to
  // the exact one) across the provider id/name/label and the surface id.
  const visibleSurfaces = useMemo(() => {
    const q = surfaceQuery.trim().toLowerCase();
    if (!q) return focusedSurfaces;
    return focusedSurfaces.filter((surface) =>
      [
        surface.providerSurfaceId,
        surface.providerSurfaceName,
        surface.providerSurfaceLabel,
        surface.surfaceId,
      ].some((value) => String(value || "").toLowerCase().includes(q)));
  }, [focusedSurfaces, surfaceQuery]);

  const selectableFocusedSurfaces = useMemo(
    () =>
      visibleSurfaces.filter(
        (surface) =>
          surface.selectable !== false &&
          surface.registrationStatus !== "registered",
      ),
    [visibleSurfaces],
  );

  const toggleSurface = (surfaceId: string) => {
    const surface = focusedSurfaces.find((item) => item.surfaceId === surfaceId);
    if (!surface || surface.selectable === false || surface.registrationStatus === "registered") return;
    setSelectedSurfaceIds((prev) => {
      const next = new Set(prev);
      if (next.has(surfaceId)) next.delete(surfaceId);
      else next.add(surfaceId);
      return next;
    });
  };

  const selectAllFocusedSurfaces = () => {
    setSelectedSurfaceIds(
      new Set(selectableFocusedSurfaces.map((surface) => surface.surfaceId)),
    );
  };

  const clearFocusedSurfaceSelection = () => {
    setSelectedSurfaceIds(new Set());
  };

  const chooseFocusedInstance = async () => {
    if (!focusedInstanceId) return;
    const surfaceIds = [...selectedSurfaceIds];
    const selectedLabels = focusedSurfaces
      .filter((surface) => selectedSurfaceIds.has(surface.surfaceId))
      .map((surface) => connectorSurfaceTitle(surface));
    const selectionLabel = selectedLabels.slice(0, 2).join(", ");
    const selectionSuffix = selectedLabels.length > 2 ? ` and ${selectedLabels.length - 2} more` : "";
    try {
      setIsRegisteringSurfaces(true);
      setStatusMessage({
        type: "waiting",
        title: "Registering surface",
        message: selectionLabel
          ? `Saving ${selectionLabel}${selectionSuffix}. The artifact will appear here as soon as the workspace accepts it.`
          : "Saving the selected connector surfaces. The artifact will appear here as soon as the workspace accepts it.",
      });
      const result = await integrationService.registerSurfaces({
        instanceId: focusedInstanceId,
        surfaceIds,
        conversationId,
      });
      setStatusMessage({
        type: "waiting",
        title: "Publishing artifact",
        message: selectionLabel
          ? `${selectionLabel}${selectionSuffix} is registered. World is preparing the workspace artifact.`
          : "The selected connector surfaces are registered. World is preparing the workspace artifact.",
      });
      await onRegisteredSurfaces?.(result);
      onChanged?.();
      setStatusMessage({
        type: "success",
        title: "Surface registered",
        message: selectionLabel
          ? `${selectionLabel}${selectionSuffix} is registered. The artifact will appear from World and keep filling as data arrives.`
          : "The selected connector surfaces are registered. Their artifacts will appear from World and keep filling as data arrives.",
      });
      window.setTimeout(() => {
        setStatusMessage(null);
        onClose?.();
      }, 1800);
    } catch (err: any) {
      setStatusMessage({
        type: "error",
        title: "Surface registration failed",
        message: connectorErrorMessage(err, "Failed to register connector surfaces"),
      });
    } finally {
      setIsRegisteringSurfaces(false);
    }
  };

  const surfaceViewportRef = useRef<HTMLDivElement | null>(null);
  const [surfaceViewportSize, setSurfaceViewportSize] = useState({
    width: 0,
    height: 0,
  });
  const [surfaceScrollTop, setSurfaceScrollTop] = useState(0);

  useEffect(() => {
    const node = surfaceViewportRef.current;
    if (!node) return;

    const updateSize = () => {
      setSurfaceViewportSize((prev) => {
        const next = {
          width: node.clientWidth,
          height: node.clientHeight,
        };
        if (prev.width === next.width && prev.height === next.height) return prev;
        return next;
      });
    };

    updateSize();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateSize);
      return () => window.removeEventListener("resize", updateSize);
    }

    const observer = new ResizeObserver(updateSize);
    observer.observe(node);
    return () => observer.disconnect();
  }, [focusedInstanceId, isLoadingSurfaces]);

  useEffect(() => {
    setSurfaceScrollTop(0);
    if (surfaceViewportRef.current) {
      surfaceViewportRef.current.scrollTop = 0;
    }
  }, [focusedInstanceId, surfaceQuery]);

  useEffect(() => {
    setSurfaceQuery("");
  }, [focusedInstanceId]);

  const surfaceColumnCount = surfaceViewportSize.width >= 640 ? 2 : 1;
  const surfaceRowCount = Math.ceil(visibleSurfaces.length / surfaceColumnCount);
  const virtualSurfaceHeight = Math.max(
    0,
    surfaceRowCount * SURFACE_ROW_STRIDE - SURFACE_ROW_GAP,
  );
  const virtualSurfaceStartRow = Math.max(
    0,
    Math.floor(surfaceScrollTop / SURFACE_ROW_STRIDE) - SURFACE_OVERSCAN_ROWS,
  );
  const virtualSurfaceEndRow = Math.min(
    surfaceRowCount,
    Math.ceil(
      (surfaceScrollTop + (surfaceViewportSize.height || 420)) /
        SURFACE_ROW_STRIDE,
    ) + SURFACE_OVERSCAN_ROWS,
  );
  const virtualSurfaceRows = useMemo(() => {
    const rows: number[] = [];
    for (let row = virtualSurfaceStartRow; row < virtualSurfaceEndRow; row += 1) {
      rows.push(row);
    }
    return rows;
  }, [virtualSurfaceEndRow, virtualSurfaceStartRow]);

  if (!isOpen) return null;

  const focusedInstanceContent = (
    <div
      className="flex max-h-[min(68vh,760px)] min-h-0 flex-col rounded-lg border p-3"
      style={{
        background: "var(--bg-secondary)",
        borderColor: "var(--border-primary)",
      }}
    >
      {isLoadingSurfaces && focusedSurfaces.length === 0 ? (
        <div className="text-sm" style={{ color: "var(--text-muted)" }}>
          Loading connector surfaces...
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="mb-2 flex shrink-0 items-center gap-2">
            <input
              type="search"
              value={surfaceQuery}
              onChange={(event) => setSurfaceQuery(event.target.value)}
              placeholder="Search surfaces (e.g. ORDERS)"
              autoFocus
              onKeyDown={(event) => {
                // narrowed to EXACTLY one selectable → Enter selects it.
                if (event.key === "Enter" && selectableFocusedSurfaces.length === 1) {
                  event.preventDefault();
                  toggleSurface(selectableFocusedSurfaces[0].surfaceId);
                }
              }}
              className="w-full rounded-lg border px-3 py-2 text-sm outline-none"
              style={{
                background: "var(--bg-tertiary)",
                borderColor: "var(--border-secondary)",
                color: "var(--text-primary)",
              }}
            />
            <span className="shrink-0 text-xs" style={{ color: "var(--text-muted)" }}>
              {visibleSurfaces.length.toLocaleString()} / {focusedSurfaces.length.toLocaleString()}
              {isLoadingSurfaces ? " loading" : ""}
            </span>
          </div>
          <div
            ref={surfaceViewportRef}
            className="h-[min(52vh,560px)] min-h-[220px] overflow-y-auto pr-1"
            onScroll={(event) => setSurfaceScrollTop(event.currentTarget.scrollTop)}
          >
            <div
              className="relative"
              style={{
                height: virtualSurfaceHeight + (isLoadingSurfaces && focusedSurfaces.length > 0 ? 36 : 0),
              }}
            >
              {virtualSurfaceRows.map((row) => (
                <div
                  key={row}
                  className={`absolute left-0 right-0 grid gap-3 ${
                    surfaceColumnCount === 2 ? "grid-cols-2" : "grid-cols-1"
                  }`}
                  style={{
                    height: SURFACE_TILE_HEIGHT,
                    top: row * SURFACE_ROW_STRIDE,
                  }}
                >
                  {Array.from({ length: surfaceColumnCount }).map((_, column) => {
                    const surfaceIndex = row * surfaceColumnCount + column;
                    const surface = visibleSurfaces[surfaceIndex];
                    if (!surface) return <span key={column} />;

                    const filter = CONNECTOR_FILTERS.find((item) => item.id === surface.kind);
                    const Icon = filter?.Icon || Database;
                    const selected = selectedSurfaceIds.has(surface.surfaceId);
                    const registered =
                      surface.registrationStatus === "registered" ||
                      surface.selectable === false;
                    const surfaceTitle = connectorSurfaceDisplayTitle(surface);
                    const surfaceDescription =
                      connectorSurfaceDisplayDescription(surface);

                    return (
                      <label
                        key={surface.surfaceId}
                        className={`flex min-w-0 items-center gap-3 rounded-lg border px-3 py-2 ${
                          registered
                            ? "cursor-not-allowed opacity-60"
                            : "cursor-pointer"
                        }`}
                        style={{
                          background: selected ? "var(--modal-bg)" : "transparent",
                          borderColor: selected
                            ? "#6B7EEA"
                            : "var(--border-secondary)",
                        }}
                      >
                        <input
                          type="checkbox"
                          className="shrink-0"
                          checked={selected}
                          disabled={registered}
                          onChange={() => toggleSurface(surface.surfaceId)}
                        />
                        <Icon
                          className="h-4 w-4 shrink-0"
                          style={{ color: "var(--text-secondary)" }}
                        />
                        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <span className="flex min-w-0 items-center gap-2">
                            <span
                              className="block min-w-0 flex-1 truncate text-sm font-semibold leading-5"
                              style={{ color: "var(--text-primary)" }}
                            >
                              {surfaceTitle}
                            </span>
                            <span
                              className="shrink-0 rounded px-1.5 py-0.5 text-[10px]"
                              style={{
                                background: registered
                                  ? "rgba(34,197,94,0.14)"
                                  : "var(--bg-tertiary)",
                                color: registered ? "#22c55e" : "var(--text-secondary)",
                              }}
                            >
                              {registered ? "Registered" : "Available"}
                            </span>
                          </span>
                          <span
                            className="block min-w-0 truncate text-xs leading-4"
                            style={{ color: "var(--text-muted)" }}
                          >
                            {surfaceDescription}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              ))}
              {isLoadingSurfaces && focusedSurfaces.length > 0 && (
                <div
                  className="absolute left-0 right-0 flex items-center justify-center gap-2 text-xs"
                  style={{
                    top: virtualSurfaceHeight,
                    height: 32,
                    color: "var(--text-muted)",
                  }}
                >
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading more surfaces...
                </div>
              )}
            </div>
          </div>
          {focusedSurfaces.length === 0 && (
            <div className="text-sm" style={{ color: "var(--text-muted)" }}>
              No connector surfaces are available for this instance yet.
            </div>
          )}
          {focusedSurfaces.length > 0 && visibleSurfaces.length === 0 && (
            <div className="text-sm" style={{ color: "var(--text-muted)" }}>
              {`No surfaces match "${surfaceQuery.trim()}".`}
            </div>
          )}
          <div
            className="mt-3 flex shrink-0 justify-end gap-2 border-t pt-3"
            style={{ borderColor: "var(--border-secondary)" }}
          >
            <button
              type="button"
              className="rounded-lg px-4 py-2 text-sm"
              style={{ background: "var(--bg-tertiary)", color: "var(--text-primary)" }}
              onClick={selectAllFocusedSurfaces}
            >
              All
            </button>
            <button
              type="button"
              className="rounded-lg px-4 py-2 text-sm"
              style={{ background: "var(--bg-tertiary)", color: "var(--text-primary)" }}
              disabled={selectedSurfaceIds.size === 0}
              onClick={clearFocusedSurfaceSelection}
            >
              Clear
            </button>
            <button
              type="button"
              className="rounded-lg px-4 py-2 text-sm font-medium"
              style={{ background: "var(--accent-blue)", color: "white" }}
              disabled={isRegisteringSurfaces || focusedSurfaces.length === 0 || selectedSurfaceIds.size === 0}
              onClick={() => void chooseFocusedInstance()}
            >
              {isRegisteringSurfaces ? "Saving..." : "Done"}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const listContent = (
    <div className={surface === "section" ? "space-y-4" : "app-card p-8"}>
      {/* Header */}
      {showHeader && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2
              className="font-semibold text-2xl mb-2"
              style={{ color: 'var(--text-primary)' }}
            >
              Connectors
            </h2>
            <p style={{ color: 'var(--text-secondary)' }}>
              Connect your business tools and services
            </p>
          </div>
          <button
            className="flex items-center justify-center gap-2 px-5 py-2.5 hover:bg-[#6B7EEA]/10 border hover:border-[#6B7EEA]/50 hover:text-[#6B7EEA] rounded-lg transition-all duration-200 active:scale-95 hover:shadow-lg hover:shadow-[#6B7EEA]/20 w-full sm:w-auto"
            style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
            onClick={onAddSource}
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm font-medium">Add connector</span>
          </button>
        </div>
      )}

      {/* Connected connectors */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
        {instances.map((inst) => (
          <div
            key={inst.instanceId}
            className="p-4 rounded-lg border min-w-0"
            style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--border-primary)' }}
          >
            <div className="flex flex-col gap-4">
              <div className="flex items-start justify-between gap-3">
                <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent-blue)' }}>
                  <Building className="w-5 h-5 text-white" />
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {inst.status === "connected" && (
                    <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center" title="Connected">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                  )}
                  {inst.status === "connecting" && (
                    <div className="w-6 h-6 bg-yellow-600 rounded-full flex items-center justify-center" title="Connecting">
                      <Loader2 className="w-4 h-4 text-white animate-spin" />
                    </div>
                  )}
                  {inst.status === "selectionPending" && (
                    <div
                      className="w-6 h-6 bg-orange-600 rounded-full flex items-center justify-center"
                      title="Action required to finish setup"
                    >
                      <AlertCircle className="w-4 h-4 text-white" />
                    </div>
                  )}
                  {(inst.status === "disconnected" || inst.status === "error") && (
                    <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center" title={inst.status === "error" ? "Connection error" : "Disconnected"}>
                      <AlertCircle className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <button
                    className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => disconnectInstance(inst.instanceId)}
                    disabled={disconnectingIds.has(inst.instanceId)}
                    title={
                      disconnectingIds.has(inst.instanceId)
                        ? "Disconnecting..."
                        : "Disconnect"
                    }
                  >
                    {disconnectingIds.has(inst.instanceId) ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="min-w-0 space-y-2">
                <h3 className="text-base font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                  {inst.displayInfo?.title || inst.instanceName || inst.serverName}
                </h3>
                <p className="text-[#6B7EEA] text-sm font-medium line-clamp-2">
                  {inst.displayInfo?.subtitle}
                </p>
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                  {inst.status === "connected" &&
                    `Created ${formatDate(inst.createdAt)}`}
                  {inst.status === "connecting" &&
                    "Completing authentication..."}
                  {inst.status === "selectionPending" &&
                    "Finish setup to connect this connector"}
                  {inst.status === "disconnected" && "Disconnected"}
                  {inst.status === "error" && "Connection error"}
                </p>
              </div>

              <div>
                {inst.status === "selectionPending" && (
                  <button
                    className="w-full px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors text-sm font-medium"
                    onClick={() => {
                      setSelectionInstanceId(inst.instanceId);
                      setShowSelectionModal(true);
                    }}
                  >
                    Finish Setup
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {isLoadingConnections && (
          <div className="text-center py-12 min-h-[280px] flex flex-col items-center justify-center md:col-span-2 xl:col-span-3" style={{ color: 'var(--text-muted)' }}>
            <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin" style={{ color: 'var(--text-muted)' }} />
            <p className="text-lg mb-2">Loading connections...</p>
          </div>
        )}

        {error && !isLoadingConnections && (
          <div className="text-center py-12 min-h-[280px] flex flex-col items-center justify-center md:col-span-2 xl:col-span-3" style={{ color: 'var(--text-muted)' }}>
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
            <p className="text-lg mb-2 text-red-400">
              Error loading connections
            </p>
            <p className="text-sm mb-4">{error}</p>
            <button
              onClick={loadInstances}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {!isLoadingConnections && !error && instances.length === 0 && (
          <div className="text-center py-12 min-h-[280px] flex flex-col items-center justify-center md:col-span-2 xl:col-span-3" style={{ color: 'var(--text-muted)' }}>
            <Settings className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
            <p className="text-lg mb-2">No connectors connected yet</p>
            <p className="text-sm">No instances available to connect.</p>
          </div>
        )}
      </div>

      {/* Status Modal */}
      {statusMessage && (
        <StatusModal
          isOpen={!!statusMessage}
          type={statusMessage.type}
          title={statusMessage.title}
          message={statusMessage.message}
          onClose={() => setStatusMessage(null)}
          autoHideDuration={statusMessage.type === "waiting" ? 0 : statusMessage.type === "success" ? 3000 : 5000}
        />
      )}
    </div>
  );

  const addModal = (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[var(--modal-backdrop)] backdrop-blur-sm">
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-[var(--modal-bg)] rounded-xl border border-[var(--border-primary)] shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-lg transition-all duration-200"
          style={{ color: 'var(--text-muted)' }}
          aria-label="Close modal"
          type="button"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="p-6 sm:p-8">
          <div className="mb-6 flex flex-col gap-4 pr-6 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-2xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                Add connector
              </h2>
              <p style={{ color: 'var(--text-secondary)' }}>
                {focusedInstanceId
                  ? `Choose which ${focusedConnectorName} surfaces to connect.`
                  : "Choose a connector to connect."}
              </p>
            </div>
          </div>
          {requireAnchor && (
            <div
              className="mb-5 rounded-lg border px-4 py-3 text-sm"
              style={{
                borderColor: "var(--border-primary)",
                background: "var(--bg-secondary)",
                color: "var(--text-secondary)",
              }}
            >
              {onboardingError ||
                "General Ledger is required to start the financial model. Connect an ERP connector that exposes GL data."}
            </div>
          )}
          {focusedInstanceId ? focusedInstanceContent : availableSourcesContent}
        </div>
      </div>
    </div>
  );

  // Render selection modal (always as overlay)
  const selectionModal = showSelectionModal && selectionInstanceId && (
    <IntegrationSelectionModal
      isOpen={true}
      onClose={() => {
        setShowSelectionModal(false);
        setSelectionInstanceId(null);
      }}
      focusedInstanceId={selectionInstanceId}
      instance={instances.find((i) => i.instanceId === selectionInstanceId)}
      onConnectionSuccess={() => {
        setShowSelectionModal(false);
        setSelectionInstanceId(null);
        loadInstances();
      }}
    />
  );

  const customModal = showCustomModal && customServer && (
    <CustomIntegrationModal
      isOpen={true}
      onClose={resetCustomModal}
      title={customServer.displayInfo?.title || customServer.name}
      subtitle={customServer.displayInfo?.subtitle}
      type={customServer.auth?.type || "custom"}
      fields={customServer.auth?.fields || []}
      initialInstanceLabel={
        typeof customServer.additional?.requestedConnectorName === "string"
          ? customServer.additional.requestedConnectorName
          : undefined
      }
      showAllowedIps={customServer.auth?.showAllowedIps}
      allowedEgressIps={customServer.allowedEgressIps}
      sandboxEnabled={customServer.sandbox?.enabled === true}
      initialAccess={NEW_INSTANCE_ACCESS}
      onSubmit={handleCustomIntegrationSubmit}
      onSuccess={async () => {
        const pendingStatus = customSubmitResultRef.current;
        const connectedInstanceId = connectedInstanceIdRef.current;
        resetCustomModal();
        await loadInstances();
        onChanged?.();
        if (connectedInstanceId) {
          setFocusedInstanceId(connectedInstanceId);
        } else {
          onClose?.();
        }
        if (pendingStatus) {
          setStatusMessage(pendingStatus);
          setTimeout(
            () => setStatusMessage(null),
            pendingStatus.type === "success" ? 3000 : 5000
          );
          customSubmitResultRef.current = null;
        }
      }}
    />
  );

  if (variant === "addModal") {
    return (
      <>
        {addModal}
        {statusMessage && (
          <StatusModal
            isOpen={!!statusMessage}
            type={statusMessage.type}
            title={statusMessage.title}
            message={statusMessage.message}
            onClose={() => setStatusMessage(null)}
            autoHideDuration={statusMessage.type === "waiting" ? 0 : statusMessage.type === "success" ? 3000 : 5000}
          />
        )}
        {selectionModal}
        {customModal}
      </>
    );
  }

  // Otherwise render embedded
  return (
    <>
      {listContent}
      {selectionModal}
      {customModal}
    </>
  );
};
